<script>
import api from "@/services/http";

const STORAGE_KEY = "worksched.rightSidebarCollapsed";

// Module-level cache: every page wraps its own <Layout>, so route navigation
// remounts this component. Without this cache, /api/quick-actions would be hit
// on every page transition. Cleared on logout via clearQuickActionsCache().
let tilesCache = null;

export function clearQuickActionsCache() {
  tilesCache = null;
}

export default {
  data() {
    return {
      tiles: [],
      collapsed: false,
      loading: true,
    };
  },
  async mounted() {
    this.collapsed = localStorage.getItem(STORAGE_KEY) === "1";
    if (tilesCache) {
      this.tiles = tilesCache;
      this.loading = false;
      return;
    }
    this.loading = true;
    try {
      const { data } = await api.get("/quick-actions");
      tilesCache = data?.data || [];
      this.tiles = tilesCache;
    } catch (e) {
      this.tiles = [];
    } finally {
      this.loading = false;
    }
  },
  methods: {
    toggle() {
      this.collapsed = !this.collapsed;
      localStorage.setItem(STORAGE_KEY, this.collapsed ? "1" : "0");
    },
  },
};
</script>

<template>
  <aside
    class="ws-right-sidebar d-none d-xl-flex"
    :class="{ 'ws-right-sidebar--collapsed': collapsed }"
  >
    <button
      type="button"
      class="ws-right-sidebar__toggle"
      :title="collapsed ? 'Show quick actions' : 'Hide quick actions'"
      @click="toggle"
    >
      <i :class="collapsed ? 'bx bx-chevron-left' : 'bx bx-chevron-right'"></i>
    </button>
    <nav v-show="!collapsed" class="ws-right-sidebar__tiles">
      <template v-if="loading">
        <div v-for="n in 7" :key="`skel-${n}`" class="ws-right-sidebar__tile ws-rs-skel">
          <span class="ws-right-sidebar__icon ws-rs-shimmer"></span>
          <span class="ws-rs-skel__text ws-rs-shimmer" :style="{ width: 50 + (n % 4) * 12 + '%' }"></span>
        </div>
      </template>
      <router-link
        v-for="tile in tiles"
        v-else
        :key="tile.label"
        :to="tile.route"
        class="ws-right-sidebar__tile"
      >
        <span class="ws-right-sidebar__icon" :style="{ background: tile.color }">
          <i :class="tile.icon"></i>
        </span>
        <span class="ws-right-sidebar__label">{{ tile.label }}</span>
      </router-link>
    </nav>
  </aside>
</template>

<style scoped>
.ws-right-sidebar {
  flex-direction: column;
  width: 290px;
  flex-shrink: 0;
  align-self: flex-start;
  background: transparent;
  padding: 0.25rem 0 0.25rem 0.5rem;
  position: sticky;
  top: 90px;
  transition: width 0.2s ease;
}
.ws-right-sidebar--collapsed {
  width: 0;
  padding: 0;
}
.ws-right-sidebar__toggle {
  position: fixed;
  top: 50%;
  right: 296px;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 0;
  background: #F57C00;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  z-index: 1003;
  padding: 0;
  transition: right 0.2s ease;
}
.ws-right-sidebar__toggle:hover {
  filter: brightness(1.08);
}
.ws-right-sidebar--collapsed .ws-right-sidebar__toggle {
  right: 0;
}
.ws-right-sidebar__tiles {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.ws-right-sidebar__tile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  text-decoration: none;
  color: var(--bs-body-color);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  border: 1px solid var(--bs-border-color);
}
.ws-right-sidebar__tile:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  color: var(--bs-body-color);
}
.ws-right-sidebar__tile.router-link-active {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 1px var(--bs-primary), 0 4px 12px rgba(245, 124, 0, 0.18);
}
.ws-right-sidebar__icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.2rem;
  flex-shrink: 0;
}
.ws-right-sidebar__label {
  font-size: 0.9rem;
  font-weight: 500;
}

/* Skeleton tiles — shown while /quick-actions is being fetched. Mirrors the
   real tile layout so the loaded items don't make the column jump. */
.ws-rs-skel {
  pointer-events: none;
}
.ws-rs-skel .ws-right-sidebar__icon {
  background: transparent !important;
}
.ws-rs-skel__text {
  height: 10px;
  border-radius: 4px;
  display: inline-block;
}
.ws-rs-shimmer {
  background: linear-gradient(90deg, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.16) 50%, rgba(15, 23, 42, 0.08) 100%);
  background-size: 200% 100%;
  animation: ws-rs-shimmer-slide 1.4s ease-in-out infinite;
}
@keyframes ws-rs-shimmer-slide {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>

<style>
.ws-page-wrapper {
  display: flex;
  align-items: stretch;
  gap: 1rem;
  width: 100%;
}
.ws-page-wrapper__main {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
